import axios from 'axios';
import { getLang, getMID, getUToken, getUUID } from 'lib/evi-api-gateway';

const RESOURCE_ROOT = (process.env.REACT_APP_API_URL_RESOURCE || '')
  .replace('$DOMAIN', 'evischool.com.cn')
  .replace(/\/$/, '');
const CHINESE_WORD_BASE = `${RESOURCE_ROOT}/api_v2/item/Chinese_word`;
const CHINESE_WORD_CHAR_BASE = `${RESOURCE_ROOT}/api_v2/item/Chinese_word_char`;
const CHINESE_WORD_CHAR_AUDIO_BASE = `${RESOURCE_ROOT}/api_v2/item/Chinese_word_char_audio`;
const CHINESE_WORD_CHAR_MAP_BASE = `${RESOURCE_ROOT}/api_v2/item/Chinese_word_char_map`;

export type ChineseDictionaryReading = {
  display: string;
  code: string;
  audio_id?: string;
  sound?: string;
  audio_url?: string;
};

export type ChineseDictionaryPhrase = {
  phrase: string;
  english?: string;
  sentence?: string;
  putonghua?: ChineseDictionaryReading;
  cantonese?: ChineseDictionaryReading;
};

export type ChineseDictionaryCharacter = {
  id: string;
  word: string;
  radical?: string;
  stroke?: number;
  english?: string;
  cj_code?: string;
  cj_root?: string;
  is_hd?: boolean;
  stroke_images: string[];
  hres_images: string[];
  readings: {
    putonghua: ChineseDictionaryReading[];
    cantonese: ChineseDictionaryReading[];
  };
  variants: string[];
  remarks: string[];
  phrases: ChineseDictionaryPhrase[];
};

export type ChineseDictionarySearchResult = {
  query: string;
  selectedIndex: number;
  characters: ChineseDictionaryCharacter[];
};

export type ChineseDictionarySearchParams = {
  word: string;
  pos?: number;
};

export type ChineseDictionaryListParams = {
  search?: string;
  limit?: number;
  offset?: number;
};

export type ChineseDictionaryListResult = {
  total: number;
  rows: ChineseDictionaryCharacter[];
};

export type ChineseDictionaryAddParams = {
  word: string;
  putonghuaPinyin: string;
  cantoneseJyutping: string;
  audioMap?: Array<{ audio_id: string; sort: number }>;
};

export type ChineseWordCharMapRow = {
  map_id?: number;
  sort: number;
  char_audio_id: number;
  char?: string;
  audio_file?: string | null;
  audio_status?: string;
  url?: string | null;
};

export type T6CombineReadiness = {
  ready: boolean;
  reason?: string;
  positions: Array<{
    sort: number;
    char: string;
    char_audio_id: number;
    hasFile: boolean;
  }>;
};

type MainResourceEnvelope<T> = {
  result?: number;
  data?: T;
  ret?: T;
  message?: string;
  error?: string;
};

type ChineseWordCharListRow = {
  id?: string | number;
  item_id?: string | number;
  res_id?: string | number;
  adapter_id?: string | number;
  char?: string;
  character?: string;
  word?: string;
  name?: string;
  english?: string;
  radical?: string;
  stroke?: string | number;
  metadata?: string | Record<string, unknown>;
  char_info?: string | Record<string, unknown>;
  info?: string | Record<string, unknown>;
  jyutping?: string;
  cantonese_jyutping?: string;
  cantoneseJyutping?: string;
  yue?: unknown;
  yue_pinyin?: string;
  yuePinyin?: string;
  pinyin?: string;
  mandarin?: unknown;
  mandarin_pinyin?: string;
  mandarinPinyin?: string;
  putonghua_pinyin?: string;
  putonghuaPinyin?: string;
  putonghua?: unknown;
  cantonese?: unknown;
  audio?: ChineseWordCharAudioRow[];
  audios?: ChineseWordCharAudioRow[];
  sound?: ChineseWordCharAudioRow[];
  sounds?: ChineseWordCharAudioRow[];
  char_audio?: ChineseWordCharAudioRow[];
  char_audios?: ChineseWordCharAudioRow[];
};

type ChineseWordCharListResponse = {
  total?: number;
  rows?: ChineseWordCharListRow[];
};

type ChineseWordCharGetResponse =
  | ChineseWordCharListRow
  | {
      row?: ChineseWordCharListRow;
      item?: ChineseWordCharListRow;
      data?: ChineseWordCharListRow;
      rows?: ChineseWordCharListRow[];
    };

type ChineseWordCharAudioListResponse = {
  rows?: ChineseWordCharAudioRow[];
};

export type ChineseDictionaryGlobalAudioToken = {
  audio_id: string | number;
  sign_url: string;
  oss_path: string;
  file_name: string;
  x_oss_callback?: string;
};

type ChineseWordCharAudioRow = {
  id?: string | number;
  audio_id?: string | number;
  chinese_word_char_id?: string | number;
  word_id?: string | number;
  vo_lang?: string | number;
  lang?: string;
  language?: string;
  jyutping?: string;
  pinyin?: string;
  code?: string;
  display?: string;
  sound?: string;
  oss_url?: string;
  audio_url?: string;
  url?: string;
};

type ChineseWordSearchWordChar = ChineseWordCharListRow & {
  audios?: ChineseWordCharAudioRow[];
};

type ChineseWordSearchWordItem = {
  word?: string;
  name?: string;
  chars?: ChineseWordSearchWordChar[];
  characters?: ChineseWordSearchWordChar[];
  rows?: ChineseWordSearchWordChar[];
  items?: ChineseWordSearchWordChar[];
};

type ChineseWordSearchWordResponse = {
  created?: ChineseWordSearchWordItem[];
  existing?: ChineseWordSearchWordItem[];
  words?: ChineseWordSearchWordItem[] | { words?: ChineseWordSearchWordItem[] };
};

type ResourcePayload = Record<string, unknown>;

function splitWord(word: string) {
  return Array.from(word.trim()).filter((char) => !/\s/u.test(char));
}

function cleanString(value: unknown) {
  return value === undefined || value === null ? '' : String(value).trim();
}

function parseMetadata(value: unknown): Record<string, any> {
  if (!value) return {};
  if (typeof value === 'object') return value as Record<string, any>;
  try {
    const parsed = JSON.parse(String(value));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeTextList(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value.map(cleanString).filter(Boolean);
}

function normalizeCodeList(value: unknown): Array<Record<string, unknown>> {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((item) => (typeof item === 'object' && item ? item : { code: item }));
  }
  if (typeof value === 'object') {
    const block = value as Record<string, unknown>;
    if (Array.isArray(block.codes)) return normalizeCodeList(block.codes);
    if (Array.isArray(block.items)) return normalizeCodeList(block.items);
    if (Array.isArray(block.rows)) return normalizeCodeList(block.rows);
    if (Array.isArray(block.data)) return normalizeCodeList(block.data);
    if (Array.isArray(block.list)) return normalizeCodeList(block.list);
    if (Array.isArray(block.values)) return normalizeCodeList(block.values);
    if (Array.isArray(block.options)) return normalizeCodeList(block.options);
    return [block];
  }
  return String(value)
    .split(/[/,，、\s]+/u)
    .map((code) => cleanString(code))
    .filter(Boolean)
    .map((code) => ({ code, display: code }));
}

function readingCode(reading: Record<string, unknown>, language: 'putonghua' | 'cantonese') {
  return cleanString(
    language === 'putonghua'
      ? reading.code ||
          reading.value ||
          reading.display ||
          reading.pinyin ||
          reading.mandarin_pinyin ||
          reading.mandarinPinyin ||
          reading.putonghua_pinyin ||
          reading.putonghuaPinyin ||
          reading.mandarin
      : reading.code ||
          reading.value ||
          reading.display ||
          reading.jyutping ||
          reading.cantonese_jyutping ||
          reading.cantoneseJyutping ||
          reading.yue_pinyin ||
          reading.yuePinyin ||
          reading.yue
  );
}

function resourceAudio(
  audios: Array<Record<string, unknown>>,
  language: 'putonghua' | 'cantonese',
  code: string,
  index: number
) {
  const voLang = language === 'putonghua' ? '3' : '1';
  const matching =
    audios.find((audio) => {
      if (cleanString(audio.vo_lang) !== voLang) return false;
      const audioCode = cleanString(language === 'putonghua' ? audio.pinyin : audio.jyutping);
      return code ? audioCode === code : true;
    }) || audios.filter((audio) => cleanString(audio.vo_lang) === voLang)[index];

  return matching;
}

function normalizeReading(
  reading: Record<string, unknown>,
  language: 'putonghua' | 'cantonese',
  metadata: Record<string, any>,
  audios: Array<Record<string, unknown>>,
  word: string,
  index: number
): ChineseDictionaryReading | null {
  const code = readingCode(reading, language);
  const display = cleanString(reading.display || reading.label || reading.value || code);
  if (!display && !code) return null;

  const languageBlock =
    language === 'putonghua'
      ? metadata.putonghua || metadata.mandarin || {}
      : metadata.cantonese || {};
  const audioUrls = Array.isArray(languageBlock.audio_urls) ? languageBlock.audio_urls : [];
  const audio = resourceAudio(audios, language, code || display, index);

  return {
    display,
    code,
    sound: cleanString(reading.sound) || undefined,
    audio_id: cleanString(reading.audio_id || audio?.id) || undefined,
    audio_url: cleanString(audio?.oss_url) || cleanString(audioUrls[index]) || undefined
  };
}

function audioRowLanguage(row: ChineseWordCharAudioRow): 'putonghua' | 'cantonese' | undefined {
  const voLang = cleanString(row.vo_lang).toLowerCase();
  const language = cleanString(row.lang || row.language).toLowerCase();
  if (
    voLang === '3' ||
    voLang.includes('putonghua') ||
    voLang.includes('mandarin') ||
    language.includes('putonghua') ||
    language.includes('mandarin')
  ) {
    return 'putonghua';
  }
  if (
    voLang === '1' ||
    voLang.includes('cantonese') ||
    voLang.includes('yue') ||
    language.includes('cantonese') ||
    language.includes('yue')
  ) {
    return 'cantonese';
  }
  return undefined;
}

function normalizeAudioReading(
  row: ChineseWordCharAudioRow,
  language: 'putonghua' | 'cantonese'
): ChineseDictionaryReading | null {
  const code = cleanString(
    language === 'putonghua'
      ? row.pinyin || row.code || row.display || row.jyutping
      : row.jyutping || row.code || row.display || row.pinyin
  );
  const display = cleanString(row.display || code);
  if (!code && !display) return null;
  return {
    display: display || code,
    code: code || display,
    audio_id: cleanString(row.audio_id || row.id) || undefined,
    sound: cleanString(row.sound) || undefined,
    audio_url: cleanString(row.oss_url || row.audio_url || row.url) || undefined
  };
}

function mergeReadings(readings: ChineseDictionaryReading[]) {
  const unique = new Map<string, ChineseDictionaryReading>();
  readings.forEach((reading) => {
    const key = getReadingKey(reading);
    if (!key || unique.has(key)) return;
    unique.set(key, reading);
  });
  return Array.from(unique.values());
}

function getReadingKey(reading: ChineseDictionaryReading) {
  const code = cleanString(reading.code || reading.display);
  const audioId = cleanString(reading.audio_id);
  return audioId ? `${code}:${audioId}` : code;
}

function normalizeAudioReadings(
  rows: ChineseWordCharAudioRow[],
  language: 'putonghua' | 'cantonese'
) {
  return mergeReadings(
    rows
      .filter((row) => audioRowLanguage(row) === language)
      .map((row) => normalizeAudioReading(row, language))
      .filter(Boolean) as ChineseDictionaryReading[]
  );
}

function readingSource(
  row: ChineseWordCharListRow,
  metadata: Record<string, any>,
  language: 'putonghua' | 'cantonese'
) {
  return language === 'putonghua'
    ? row.putonghua ||
        metadata.putonghua ||
        row.mandarin ||
        metadata.mandarin ||
        row.pinyin ||
        row.mandarin_pinyin ||
        row.mandarinPinyin ||
        row.putonghua_pinyin ||
        row.putonghuaPinyin ||
        metadata.pinyin ||
        metadata.mandarin_pinyin ||
        metadata.mandarinPinyin ||
        metadata.putonghua_pinyin ||
        metadata.putonghuaPinyin
    : row.cantonese ||
        metadata.cantonese ||
        row.yue ||
        metadata.yue ||
        row.jyutping ||
        row.cantonese_jyutping ||
        row.cantoneseJyutping ||
        row.yue_pinyin ||
        row.yuePinyin ||
        metadata.jyutping ||
        metadata.cantonese_jyutping ||
        metadata.cantoneseJyutping ||
        metadata.yue_pinyin ||
        metadata.yuePinyin;
}

function normalizeInlineReadings(
  row: ChineseWordCharListRow,
  metadata: Record<string, any>,
  language: 'putonghua' | 'cantonese'
) {
  const source = readingSource(row, metadata, language);
  return mergeReadings(
    normalizeCodeList(source)
      .map((reading) => normalizeReading(reading, language, metadata, [], cleanString(row.char), 0))
      .filter(Boolean) as ChineseDictionaryReading[]
  );
}

function getAudioRows(row: ChineseWordCharListRow): ChineseWordCharAudioRow[] {
  return [
    ...asArray<ChineseWordCharAudioRow>(row.audios),
    ...asArray<ChineseWordCharAudioRow>(row.audio),
    ...asArray<ChineseWordCharAudioRow>(row.sound),
    ...asArray<ChineseWordCharAudioRow>(row.sounds),
    ...asArray<ChineseWordCharAudioRow>(row.char_audio),
    ...asArray<ChineseWordCharAudioRow>(row.char_audios)
  ];
}

function attachAudioReadings(
  inlineReadings: ChineseDictionaryReading[],
  audioReadings: ChineseDictionaryReading[]
) {
  if (!inlineReadings.length) return audioReadings;
  if (!audioReadings.length) return inlineReadings;
  return audioReadings.map((audioReading) => {
    const audioCode = cleanString(audioReading.code || audioReading.display);
    const metadataReading = inlineReadings.find(
      (reading) => cleanString(reading.code || reading.display) === audioCode
    );
    return metadataReading
      ? {
          ...metadataReading,
          audio_id: audioReading.audio_id,
          audio_url: audioReading.audio_url
        }
      : audioReading;
  });
}

function normalizeChineseWordChar(
  row: ChineseWordCharListRow,
  audioRows: ChineseWordCharAudioRow[] = []
): ChineseDictionaryCharacter | null {
  const metadata = parseMetadata(row.metadata || row.char_info || row.info);
  const word = cleanString(metadata.character || row.char || row.character || row.word || row.name);
  if (!word) return null;
  const audioPutonghua = normalizeAudioReadings(audioRows, 'putonghua');
  const audioCantonese = normalizeAudioReadings(audioRows, 'cantonese');
  const inlinePutonghua = normalizeInlineReadings(row, metadata, 'putonghua');
  const inlineCantonese = normalizeInlineReadings(row, metadata, 'cantonese');

  return {
    id: cleanString(row.id || metadata.id || word),
    word,
    radical: cleanString(metadata.radical || row.radical) || undefined,
    stroke: Number.isFinite(Number(metadata.stroke || row.stroke))
      ? Number(metadata.stroke || row.stroke)
      : undefined,
    english: cleanString(metadata.english || row.english) || undefined,
    cj_code: cleanString(metadata.cj_code || metadata.cangjie_code) || undefined,
    cj_root: cleanString(metadata.cj_root || metadata.cangjie_root) || undefined,
    is_hd: Boolean(metadata.is_hd || metadata.ishd),
    stroke_images: [],
    hres_images: [],
    readings: {
      putonghua: attachAudioReadings(inlinePutonghua, audioPutonghua),
      cantonese: attachAudioReadings(inlineCantonese, audioCantonese)
    },
    variants: normalizeTextList(metadata.variants || metadata.ytz),
    remarks: normalizeTextList(metadata.remarks || metadata.zx_rmk),
    phrases: []
  };
}

function normalizeChineseWordCharDetail(
  response: ChineseWordCharGetResponse | undefined
): ChineseDictionaryCharacter | undefined {
  if (!response || typeof response !== 'object') return undefined;
  const body = response as Exclude<ChineseWordCharGetResponse, ChineseWordCharListRow>;
  const row =
    body.row ||
    body.item ||
    body.data ||
    (Array.isArray(body.rows) ? body.rows[0] : undefined) ||
    (response as ChineseWordCharListRow);
  return normalizeChineseWordChar(row, getAudioRows(row)) || undefined;
}

function buildFormBody(data: ResourcePayload) {
  const params = new URLSearchParams();
  const auth: Record<string, string> = {
    lang: String(getLang() || ''),
    device_id: String(getUUID() || ''),
    access_token: String(getUToken() || ''),
    merchant_id: String(getMID() || '')
  };

  Object.entries(auth).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (key === 'audio_map' && Array.isArray(value)) {
      value.forEach((item, index) => {
        if (!item || typeof item !== 'object') return;
        Object.entries(item as Record<string, unknown>).forEach(([itemKey, itemValue]) => {
          if (itemValue === undefined || itemValue === null || itemValue === '') return;
          params.append(`${key}[${index}][${itemKey}]`, String(itemValue));
        });
      });
      return;
    }
    params.append(key, typeof value === 'string' ? value : JSON.stringify(value));
  });

  return { params, auth };
}

function unwrapMainResource<T>(payload: MainResourceEnvelope<T>): T {
  if (!payload || Number(payload.result) !== 1) {
    throw new Error(payload?.message || payload?.error || 'Resource API request failed');
  }
  return (payload.data !== undefined ? payload.data : payload.ret) as T;
}

/**
 * Resource `Chinese_word_char/add` never returns `id`.
 * `create_manual_word` inserts one `chinese_word_char` row and puts that
 * auto-increment PK in `chinese_word_item_id` (name leftover from
 * `resource_chinese_word_item`). `id` / `char_id` stay as fallbacks for
 * older or nested envelopes. `map_ids` are char-map row ids, not the word.
 */
function firstAddedWordBankId(record: Record<string, unknown>) {
  return [record.chinese_word_item_id, record.id, record.char_id].find(
    (value) => value != null && value !== ''
  );
}

function parseAddedWordBankId(result: unknown): string {
  if (typeof result === 'number' && Number.isFinite(result)) {
    return String(result);
  }
  if (typeof result === 'string') {
    const text = result.trim();
    if (text) return text;
  }
  if (result && typeof result === 'object') {
    const record = result as Record<string, unknown>;
    const id = firstAddedWordBankId(record);
    if (id != null) {
      return String(id);
    }
    if (record.data != null) {
      return parseAddedWordBankId(record.data);
    }
  }
  throw new Error('Chinese_word_char/add returned no id');
}

async function postResource<T>(baseUrl: string, path: string, data: ResourcePayload = {}) {
  const { params } = buildFormBody(data);
  const response = await axios.post<MainResourceEnvelope<T>>(
    `${baseUrl}/${path}`,
    params.toString(),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }
  );
  return unwrapMainResource<T>(response.data);
}

function postChineseWordChar<T>(path: string, data: ResourcePayload = {}) {
  return postResource<T>(CHINESE_WORD_CHAR_BASE, path, data);
}

function postChineseWord<T>(path: string, data: ResourcePayload = {}) {
  return postResource<T>(CHINESE_WORD_BASE, path, data);
}

function postChineseWordCharAudio<T>(path: string, data: ResourcePayload = {}) {
  return postResource<T>(CHINESE_WORD_CHAR_AUDIO_BASE, path, data);
}

function postChineseWordCharMap<T>(path: string, data: ResourcePayload = {}) {
  return postResource<T>(CHINESE_WORD_CHAR_MAP_BASE, path, data);
}

function getErrorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown; error?: unknown } | undefined;
    const serverMessage = data?.message || data?.error;
    if (serverMessage) return String(serverMessage);
    if (error.message) return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function asArray<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function looksLikeChineseWordChar(value: unknown): value is ChineseWordSearchWordChar {
  if (!value || typeof value !== 'object') return false;
  const row = value as ChineseWordCharListRow;
  return Boolean(
    row.char ||
      row.name ||
      row.character ||
      row.metadata ||
      row.char_info ||
      row.info ||
      row.jyutping ||
      row.pinyin ||
      row.cantonese ||
      row.putonghua ||
      getAudioRows(row).length
  );
}

function getSearchWordItemChars(item: unknown): ChineseWordSearchWordChar[] {
  if (!item || typeof item !== 'object') return [];
  const row = item as ChineseWordSearchWordItem;
  const chars = [
    ...asArray<ChineseWordSearchWordChar>(row.chars),
    ...asArray<ChineseWordSearchWordChar>(row.characters),
    ...asArray<ChineseWordSearchWordChar>(row.rows),
    ...asArray<ChineseWordSearchWordChar>(row.items)
  ];
  return chars.length ? chars : looksLikeChineseWordChar(item) ? [item] : [];
}

function getSearchWordItemWord(item: unknown) {
  if (!item || typeof item !== 'object') return '';
  const row = item as ChineseWordSearchWordItem;
  return cleanString(row.word || row.name);
}

function normalizeSearchWordCharacters(
  searchWord: ChineseWordSearchWordResponse | undefined,
  query: string
) {
  const responseWordsValue = searchWord && searchWord.words;
  const responseWords =
    Array.isArray(responseWordsValue) || !responseWordsValue
      ? asArray(responseWordsValue)
      : asArray(responseWordsValue.words);
  const words = [
    ...responseWords,
    ...asArray(searchWord?.created),
    ...asArray(searchWord?.existing)
  ];
  const exactWords = words.filter((item) => getSearchWordItemWord(item) === query);
  const chars = (exactWords.length ? exactWords : words).flatMap(getSearchWordItemChars);

  return chars
    .map((char) => normalizeChineseWordChar(char, getAudioRows(char)))
    .filter(Boolean) as ChineseDictionaryCharacter[];
}

function filterCharactersToQuery(
  characters: ChineseDictionaryCharacter[],
  query: string
): ChineseDictionaryCharacter[] {
  const queryCharacters = splitWord(query);
  return queryCharacters
    .map((char) => characters.find((item) => item.word === char))
    .filter((item): item is ChineseDictionaryCharacter => Boolean(item));
}

function hasPronunciationData(character?: ChineseDictionaryCharacter) {
  return Boolean(character?.readings.putonghua.length && character.readings.cantonese.length);
}

async function searchWordFromPrimaryWordBank(word: string) {
  try {
    // Contract fix H5: `Chinese_word/search_word` only reads `words` (a non-empty
    // array, or a comma-separated string). `search` / `word` were never read —
    // dropped. A single query string is a valid one-element CSV.
    return await postChineseWord<ChineseWordSearchWordResponse>('search_word', {
      words: word
    });
  } catch (error) {
    if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
      // eslint-disable-next-line no-console
      console.warn('[chineseDictionary] search_word did not return a success envelope', error);
    }
    return undefined;
  }
}

async function loadCharacterForChar(char: string): Promise<ChineseDictionaryCharacter | undefined> {
  const list = await postChineseWordChar<ChineseWordCharListResponse>('get_list', {
    search: char,
    limit: 20,
    offset: 0,
    sort: 'id',
    order: 'desc'
  });
  const rows = Array.isArray(list?.rows) ? list.rows : [];
  let fallback: ChineseDictionaryCharacter | undefined;

  for (const row of rows) {
    const normalized = normalizeChineseWordChar(row, getAudioRows(row));
    if (normalized?.word !== char) continue;
    if (hasPronunciationData(normalized)) return normalized;
    fallback = fallback || normalized;
  }

  return fallback;
}

export const chineseDictionaryClient = {
  async list(params: ChineseDictionaryListParams = {}): Promise<ChineseDictionaryListResult> {
    const response = await postChineseWordChar<ChineseWordCharListResponse>('get_list', {
      search: params.search?.trim() || undefined,
      limit: params.limit ?? 10,
      offset: params.offset ?? 0,
      sort: 'id',
      order: 'desc'
    });
    const rows = (response?.rows || [])
      .map((row) => normalizeChineseWordChar(row, getAudioRows(row)))
      .filter(Boolean) as ChineseDictionaryCharacter[];
    return { total: Number(response?.total ?? rows.length), rows };
  },

  async get(id: string): Promise<ChineseDictionaryCharacter | undefined> {
    const response = await postChineseWordChar<ChineseWordCharGetResponse>('get', { id });
    return normalizeChineseWordCharDetail(response);
  },

  async listAudio(characterId: string): Promise<ChineseDictionaryReading[]> {
    const response = await postChineseWordCharAudio<ChineseWordCharAudioListResponse>(
      'get_list_by_char_id',
      { chinese_word_char_id: characterId }
    );
    return normalizeAudioReadings(response?.rows || [], 'cantonese');
  },

  /**
   * Per-position composition for a parent chinese_word_char.id
   * (`Chinese_word_char_map/get_list_by_parent`). Used by T6 combine preflight.
   */
  async getCharComposition(charId: string): Promise<ChineseWordCharMapRow[]> {
    const response = await postChineseWordCharMap<{ composition?: ChineseWordCharMapRow[] }>(
      'get_list_by_parent',
      { char_id: charId }
    );
    return Array.isArray(response?.composition) ? response.composition : [];
  },

  /**
   * Whether save_all can IMM-combine (or copy) char clips into
   * resource_chinese_word_audio for this bank word.
   */
  async assertT6CombineReady(charId: string): Promise<T6CombineReadiness> {
    const composition = await this.getCharComposition(charId);
    const positions = composition.map((row) => {
      const charAudioId = Number(row.char_audio_id) || 0;
      const file = String(row.audio_file || '').trim();
      const status = String(row.audio_status || '');
      const hasFile = Boolean(charAudioId && file && status !== 'INVALID');
      return {
        sort: Number(row.sort) || 0,
        char: String(row.char || ''),
        char_audio_id: charAudioId,
        hasFile
      };
    });
    if (!positions.length) {
      return { ready: false, reason: 'EMPTY_CHAR_MAP', positions };
    }
    if (positions.some((position) => !position.hasFile)) {
      return { ready: false, reason: 'MISSING_CHAR_AUDIO_CLIPS', positions };
    }
    return { ready: true, positions };
  },

  /**
   * Sign an upload for an EXISTING `chinese_word_char_audio` row.
   *
   * Contract fix C5: `Chinese_word_char_audio/oss_signurl` reads `id` — the
   * primary key of the audio row — not `chinese_word_char_id`. Passing the
   * character id made every global-audio upload fail.
   *
   * ⚠ Known backend gap: `Chinese_word_char_audio` exposes only
   * get / get_list / get_list_by_char_id / oss_signurl / update_file / update /
   * delete — there is **no create action**, so the panel cannot mint an audio
   * row for a character that has none. Audio rows are only created as
   * placeholders by `addMcChineseBundle` when a T6 question is created. Until
   * Henry adds a create endpoint (or lets sign create-from-char), uploading a
   * brand-new pronunciation from the word bank is not possible — callers must
   * resolve an existing row via `listAudio(characterId)` first.
   */
  signGlobalAudio(params: {
    /** `chinese_word_char_audio.id` — NOT the character id. */
    id: string | number;
    jyutping: string;
    file_name: string;
    meta: { size: number; type: string };
  }): Promise<ChineseDictionaryGlobalAudioToken> {
    return postChineseWordCharAudio<ChineseDictionaryGlobalAudioToken>('oss_signurl', {
      ...params,
      vo_lang: 1
    });
  },

  async upsertQuestionWord(params: {
    id?: number;
    res_id: number;
    name: string;
    jyutping: string;
    char_id?: string;
  }): Promise<number> {
    const id = await postChineseWord<number | string>('upsert', {
      ...params,
      status: 'NEW',
      ai_status: 'N'
    });
    const parsed = Number(id);
    if (!Number.isFinite(parsed)) {
      throw new Error('Chinese_word/upsert returned an invalid word id');
    }
    return parsed;
  },

  /**
   * Create a word-bank entry.
   *
   * `params.putonghuaPinyin` / `params.cantoneseJyutping` are intentionally NOT
   * forwarded: Henry's `Chinese_word_char/add` only accepts `audio_map`
   * (required, `{audio_id, sort}[]`) and `name` (optional, logged only). The
   * pronunciation is stored on the *audio* rows themselves — each
   * `Chinese_word_char_audio` row carries `jyutping` / `vo_lang` — so it reaches
   * the backend through the ids in `audio_map`, not as a field on the word.
   * The two params stay on the type because callers use them to pick which
   * audio rows to reference.
   */
  async addToWordBank(params: ChineseDictionaryAddParams): Promise<string> {
    const word = cleanString(params.word);

    return parseAddedWordBankId(
      await postChineseWordChar('add', {
        name: word,
        audio_map: params.audioMap?.length ? params.audioMap : undefined
      })
    );
  },

  async searchWord(params: ChineseDictionarySearchParams): Promise<ChineseDictionarySearchResult> {
    const query = splitWord(params.word).join('');
    if (!query) {
      throw new Error('Dictionary lookup failed');
    }
    try {
      const searchWord = await searchWordFromPrimaryWordBank(query);
      const characters = filterCharactersToQuery(
        normalizeSearchWordCharacters(searchWord, query),
        query
      );
      const requestedIndex = Number(params.pos || 0);
      const selectedIndex = Math.min(
        Math.max(Number.isFinite(requestedIndex) ? Math.floor(requestedIndex) : 0, 0),
        Math.max(characters.length - 1, 0)
      );
      return { query, selectedIndex, characters };
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Dictionary lookup failed'));
    }
  },

  async search(params: ChineseDictionarySearchParams): Promise<ChineseDictionarySearchResult> {
    const query = splitWord(params.word).join('');
    if (!query) {
      throw new Error('Dictionary lookup failed');
    }

    try {
      const searchWord = await searchWordFromPrimaryWordBank(query);
      const searchWordCharacters = normalizeSearchWordCharacters(searchWord, query);
      const queryCharacters = splitWord(query);
      const characters = (
        await Promise.all(
          queryCharacters.map(async (char, index) => {
            const searchWordCharacter =
              searchWordCharacters.find((character) => character.word === char) ||
              (searchWordCharacters.length === queryCharacters.length
                ? searchWordCharacters[index]
                : undefined);
            if (hasPronunciationData(searchWordCharacter)) return searchWordCharacter;
            let loaded: ChineseDictionaryCharacter | undefined;
            try {
              loaded = await loadCharacterForChar(char);
            } catch (error) {
              if (searchWordCharacter) return searchWordCharacter;
              throw error;
            }
            if (hasPronunciationData(loaded)) return loaded;
            return loaded || searchWordCharacter;
          })
        )
      ).filter(Boolean) as ChineseDictionaryCharacter[];
      const requestedIndex = Number(params.pos || 0);
      const selectedIndex = Math.min(
        Math.max(Number.isFinite(requestedIndex) ? Math.floor(requestedIndex) : 0, 0),
        Math.max(characters.length - 1, 0)
      );

      return {
        query,
        selectedIndex,
        characters
      };
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Dictionary lookup failed'));
    }
  }
};
